import React, { Component } from 'react';
import {
  hasSubscription, isPastDue, statusLabel, statusVariant,
  hasAssignedPlan, isStartDatePast, todayYmd, planFor, formatAmount,
  nextPaymentLabel, firstPaymentLabel,
} from '../../../util/subscriptionStatus';

/**
 * One shop's subscription. Presentational — the parent owns fetching, so the
 * refresh-after-redirect logic lives in one place.
 *
 * Billing is per SHOP (an `installer_location` row), not per installer. An
 * installer owning several shops has several subscriptions, several Stripe
 * customers, and several bank mandates.
 *
 * Two roles, two jobs. A Jobvious admin ASSIGNS a plan and a start date and
 * never reaches Checkout — an ACH debit is authorised by the account holder,
 * and staff entering a customer's bank details is not something to build. The
 * shop's owner or manager picks the interval and pays. See spec §12.
 */
class SubscriptionPanel extends Component {
  state = { tier: '', startDate: '', interval: 'month', editing: false };

  startAssign = () => {
    const { subscription } = this.props;
    this.setState({
      editing: true,
      tier: (subscription && subscription.assigned_tier) || '',
      startDate: (subscription && String(subscription.assigned_start_date || '').slice(0, 10)) || '',
    });
  };

  cancelAssign = () => this.setState({ editing: false });

  handleAssign = () => {
    const { tier, startDate } = this.state;
    if (!tier || !startDate) return;
    this.setState({ editing: false });
    this.props.onAssign(tier, startDate);
  };

  /** Distinct tiers from the PUBLIC plan list. A tier hidden from signup cannot
   *  be assigned in-app at all — that is accepted, see spec §12.3. */
  tierOptions() {
    const { plans = [] } = this.props;
    return [...new Set(plans.map(p => p.tier).filter(Boolean))];
  }

  renderAssignForm() {
    const { busy } = this.props;
    const { tier, startDate } = this.state;
    const tiers = this.tierOptions();
    // Not isStartDatePast: that helper reads `assigned_start_date` off a
    // subscription object, but `startDate` here is the in-progress form value
    // (a bare 'YYYY-MM-DD') before any Assign has been submitted.
    const past = !!startDate && startDate < todayYmd();

    return (
      <>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="sub-tier">Plan</label>
            {tiers.length === 0 ? (
              <div className="alert alert-warning mb-0 py-2 small">
                No plans are available to assign.
              </div>
            ) : (
              <select
                id="sub-tier" className="form-control" value={tier} disabled={busy}
                onChange={(e) => this.setState({ tier: e.target.value })}
              >
                <option value="">Choose a plan…</option>
                {tiers.map(t => {
                  // Indicative only — the shop picks the interval later, so a
                  // committed price cannot be shown here.
                  const monthlyRow = planFor(this.props.plans, t, 'month');
                  // A tier priced only annually has no monthly row — fall back
                  // to the annual row so the option still shows a display name
                  // and a price instead of the raw tier code.
                  const row = monthlyRow || planFor(this.props.plans, t, 'year');
                  let from = '';
                  if (monthlyRow) from = ` — from ${formatAmount(monthlyRow.amount_cents)}/month`;
                  else if (row) from = ` — from ${formatAmount(row.amount_cents)}/year`;
                  return <option key={t} value={t}>{(row && row.display_name) || t}{from}</option>;
                })}
              </select>
            )}
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="sub-start">Start date</label>
            <input
              id="sub-start" type="date" className="form-control"
              value={startDate} disabled={busy}
              onChange={(e) => this.setState({ startDate: e.target.value })}
            />
            <small className="form-text text-muted">
              The shop's first payment falls on this date. They are emailed a week before.
            </small>
          </div>
        </div>

        {past && (
          <div className="alert alert-warning py-2 small">
            That date has already passed, which is allowed — but the shop will be
            charged as soon as they set up payment, with no free period.
          </div>
        )}

        <button
          className="jv-btn jv-btn-primary mr-2"
          onClick={this.handleAssign}
          disabled={busy || !tier || !startDate}
        >
          {busy ? <><i className="fa-sharp fa-solid fa-spinner fa-spin mr-1" />Saving…</> : 'Assign plan'}
        </button>
        {this.state.editing && (
          <button className="jv-btn jv-btn-tertiary" onClick={this.cancelAssign} disabled={busy}>
            Cancel
          </button>
        )}
      </>
    );
  }

  /** Assigned but not yet subscribed. */
  renderAssigned() {
    const { subscription, plans, canPay, canAssign, busy } = this.props;
    const { interval } = this.state;
    const tier = subscription.assigned_tier;
    const row = planFor(plans, tier, interval);
    const monthly = planFor(plans, tier, 'month');
    const annual = planFor(plans, tier, 'year');
    const startsPast = isStartDatePast(subscription);

    return (
      <>
        <div className="d-flex align-items-center mb-2">
          <h5 className="mb-0 mr-2">{(row && row.display_name) || tier}</h5>
          <span className="badge badge-secondary">Not set up yet</span>
        </div>
        <p className="mb-3">
          {startsPast
            ? 'Billing starts as soon as payment is set up.'
            : firstPaymentLabel(subscription) || 'No start date set.'}
        </p>

        {canPay ? (
          <>
            <div className="form-group">
              <label className="d-block">Billing</label>
              <div className="form-check">
                <input
                  className="form-check-input" type="radio" id="sub-int-month" name="sub-interval"
                  checked={interval === 'month'} disabled={busy}
                  onChange={() => this.setState({ interval: 'month' })}
                />
                <label className="form-check-label" htmlFor="sub-int-month">
                  Monthly{monthly ? ` — ${formatAmount(monthly.amount_cents)} / month` : ''}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input" type="radio" id="sub-int-year" name="sub-interval"
                  checked={interval === 'year'} disabled={busy}
                  onChange={() => this.setState({ interval: 'year' })}
                />
                <label className="form-check-label" htmlFor="sub-int-year">
                  Annual{annual ? ` — ${formatAmount(annual.amount_cents)} / year` : ''} — 12 months for the price of 10
                </label>
              </div>
            </div>

            <button
              className="jv-btn jv-btn-primary"
              onClick={() => this.props.onSubscribe(interval)}
              disabled={busy}
            >
              {busy ? <><i className="fa-sharp fa-solid fa-spinner fa-spin mr-1" />Opening…</> : 'Set up payment'}
            </button>

            <p className="text-muted small mt-3 mb-0">
              {interval === 'year' && 'Annual is charged once, up front. '}
              You cannot change between monthly and annual after setup.
              Paid by bank debit (ACH) — cards are not accepted. Your bank is verified
              instantly where supported, otherwise by small test deposits over a few
              days, so set this up before the start date. Have a promo code? Enter it
              at checkout.
            </p>
          </>
        ) : (
          <p className="text-muted mb-0">
            Waiting for the shop to set up payment.
            {/* Indicative only, same reasoning as the assign dropdown — the
                interval (and any promo code) isn't chosen until the shop pays. */}
            {(monthly || annual) && ` Indicative price: ${
              monthly
                ? `${formatAmount(monthly.amount_cents)}/month`
                : `${formatAmount(annual.amount_cents)}/year`
            }.`}
          </p>
        )}

        {/* canAssign and canPay are built as mutually exclusive (see the props
            in ProfilePage.jsx), so gating on canAssign alone is equivalent to
            `canAssign && !canPay` without re-deriving the invariant here. */}
        {canAssign && (
          <button className="jv-btn jv-btn-secondary mt-3" onClick={this.startAssign} disabled={busy}>
            Change plan
          </button>
        )}
      </>
    );
  }

  renderActive() {
    const { subscription, canPay, busy } = this.props;
    const tier = subscription.subscription_tier || subscription.assigned_tier || 'Subscribed';
    const interval = subscription.billing_interval === 'year' ? 'annual' : 'monthly';
    const next = nextPaymentLabel(subscription);

    return (
      <>
        <div className="d-flex align-items-center mb-2">
          <h5 className="mb-0 mr-2">{tier}</h5>
          <span className="text-muted">· {interval}</span>
        </div>
        <p className="mb-2">
          <span className={`badge badge-${statusVariant(subscription)}`}>
            {statusLabel(subscription)}
          </span>
        </p>
        {next && <p className="mb-3">Next payment: <strong>{next}</strong></p>}

        {isPastDue(subscription) && (
          <div className="alert alert-danger py-2 small">
            {/* An admin has no Manage button (canPay is false for them below),
                so telling them to update the bank account instructs an action
                they cannot take here. They still need to know it failed. */}
            The last payment failed.
            {canPay && ' Update the bank account to restore billing.'}
            {' '}This does not affect your access to Jobvious.
          </div>
        )}

        {canPay && (
          <button
            className="jv-btn jv-btn-secondary"
            onClick={() => this.props.onManage(isPastDue(subscription) ? 'payment_method_update' : undefined)}
            disabled={busy}
          >
            {busy ? <><i className="fa-sharp fa-solid fa-spinner fa-spin mr-1" />Opening…</> : 'Manage payment method & invoices'}
          </button>
        )}
      </>
    );
  }

  renderBody() {
    const { subscription, canAssign } = this.props;

    if (hasSubscription(subscription)) return this.renderActive();
    if (this.state.editing) return this.renderAssignForm();
    if (hasAssignedPlan(subscription)) return this.renderAssigned();
    if (canAssign) return this.renderAssignForm();

    return (
      <p className="text-muted mb-0">
        No plan has been assigned to this shop yet. Jobvious will set this up.
      </p>
    );
  }

  render() {
    const { location, loading, error } = this.props;

    return (
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-crown mr-2" />
            Subscription{location ? ` — ${location.location_name}` : ''}
          </h4>
        </div>
        <div className="card-body">
          {!location && (
            <p className="text-muted mb-0">
              Select a location from the header to manage its subscription.
              Each shop is billed separately.
            </p>
          )}

          {location && loading && (
            <p className="text-muted mb-0">
              <i className="fa-sharp fa-solid fa-spinner fa-spin mr-2" />Loading…
            </p>
          )}

          {location && !loading && error && (
            <div className="alert alert-danger py-2 small">{error}</div>
          )}

          {location && !loading && this.renderBody()}
        </div>
      </div>
    );
  }
}

export default SubscriptionPanel;
